import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MapPin, LocateFixed, Store as StoreIcon, ArrowUpRight, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const GEO_KEY = "plazaya_geo_v1";

export default function PlazasDirectory() {
  const [plazas, setPlazas] = useState([]);
  const [geo, setGeo] = useState(null);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = (latLng) => {
    setLoading(true);
    const params = latLng ? { lat: latLng.lat, lng: latLng.lng } : {};
    api.get("/plazas/directory", { params })
      .then(r => setPlazas(r.data.plazas))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(GEO_KEY) || "null");
      if (saved?.lat) { setGeo(saved); load(saved); return; }
    } catch { /* ignore */ }
    load(null);
  }, []);

  const usarUbicacion = () => {
    if (!navigator.geolocation) { toast.error("Geolocalización no disponible"); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const g = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setGeo(g);
        localStorage.setItem(GEO_KEY, JSON.stringify(g));
        load(g);
        toast.success("Ordenando por cercanía");
      },
      () => toast.error("No se pudo obtener tu ubicación")
    );
  };

  const filtered = q ? plazas.filter(p => p.nombre.toLowerCase().includes(q.toLowerCase()) || (p.direccion || "").toLowerCase().includes(q.toLowerCase())) : plazas;

  return (
    <div className="min-h-screen bg-[#F9F9F8]">
      <Navbar />
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="overline text-zinc-500">Directorio</div>
            <h1 className="font-display font-black text-3xl lg:text-5xl tracking-tighter mt-1">Plazas registradas</h1>
            <p className="text-sm text-zinc-600 mt-2 max-w-md">Explora todos los centros comerciales que ya operan con en 90 MINUTOS. Cada plaza tiene su propio branding, catálogo y repartidores.</p>
          </div>
          <div className="flex gap-2">
            <Button data-testid="dir-locate" onClick={usarUbicacion} variant="outline" className="rounded-sm">
              <LocateFixed className="w-4 h-4 mr-1.5" /> {geo ? "Actualizar ubicación" : "Usar mi ubicación"}
            </Button>
            <Link to="/registrar-plaza">
              <Button data-testid="dir-register-plaza" className="rounded-sm bg-[#FF3366] hover:bg-[#E62958]">
                Registrar mi plaza <ArrowUpRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>

        <div className="mt-6 relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <Input data-testid="dir-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar por nombre o ciudad" className="pl-9 h-11 rounded-sm" />
        </div>

        {loading ? (
          <div className="mt-10 text-center text-zinc-500">Cargando plazas…</div>
        ) : (
          <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map(p => <PlazaCard key={p.id} plaza={p} />)}
            {filtered.length === 0 && (
              <div className="col-span-full border border-dashed border-zinc-300 rounded-sm p-16 text-center text-zinc-500">
                Ninguna plaza coincide con tu búsqueda
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function PlazaCard({ plaza }) {
  const primary = plaza.color_primary || "#FF3366";
  const accent = plaza.color_accent || "#D4FF00";
  return (
    <Link to={`/?plaza_id=${plaza.id}`} data-testid={`dir-plaza-${plaza.id}`} className="card-lift bg-white border border-zinc-200 rounded-sm overflow-hidden block">
      <div className="aspect-[16/10] relative" style={{ background: `linear-gradient(135deg, ${primary}20 0%, ${accent}30 100%)` }}>
        {plaza.logo_url ? (
          <img src={plaza.logo_url} alt={plaza.nombre} className="w-full h-full object-contain p-6" />
        ) : plaza.hero_image_url ? (
          <img src={plaza.hero_image_url} alt={plaza.nombre} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full grid place-items-center">
            <StoreIcon className="w-12 h-12" style={{ color: primary }} />
          </div>
        )}
        {plaza.en_radio === true && (
          <Badge className="absolute top-3 right-3 bg-emerald-500 text-white border-0">✓ En tu zona</Badge>
        )}
        {plaza.en_radio === false && (
          <Badge className="absolute top-3 right-3 bg-amber-500 text-white border-0">Fuera del radio</Badge>
        )}
      </div>
      <div className="p-5">
        <div className="overline text-zinc-500">{plaza.total_tiendas} tiendas</div>
        <div className="font-display font-black text-xl tracking-tighter mt-1">{plaza.nombre}</div>
        <div className="text-xs text-zinc-500 flex items-center gap-1 mt-1"><MapPin className="w-3 h-3" /> {plaza.direccion}</div>
        {plaza.tagline && <div className="mt-2 text-sm text-zinc-700 italic">"{plaza.tagline}"</div>}
        <div className="mt-4 flex items-center justify-between text-xs text-zinc-500">
          <span>Radio {plaza.radio_entrega_km} km · Envío ${plaza.costo_envio?.toFixed(2)}</span>
          {plaza.distancia_km !== null && plaza.distancia_km !== undefined && (
            <span className="font-medium text-zinc-900">{plaza.distancia_km} km</span>
          )}
        </div>
      </div>
    </Link>
  );
}
